/** ************************************************************************
 * Atomic Component for the bPerx iFrame UI.
 *
 * Atomic Type: Content, dynamic (toggle)
 * Purpose: Display/hide the terms and conditions of the offer
 *
 * DEV NOTE: In 2.2.0, this component was a link to an external page.
 *           In 2.5.0, we need to use an internal div for the terms,
 *           because of how vouchers are generated in 2.5.0.
 *           This link will now act more as a toggle, BUT, this link
 *           has a classname "BPERX_SELECTED_OFFER_TERMS_LINK" that
 *           is part of the "external api" customers use for CSS
 *           customization. We CANNOT change the class name, so,
 *           the component's name will remain as is, despite its
 *           new function as a toggle versus a link.
 *
 * @file   Atomic Component, content dynamic, offer terms
 * @author Paul J Stales
 * @since  2.2.0, changed in 2.5.0
 ************************************************************************ */

import { FunctionalComponent, h } from 'preact'
import { css } from 'aphrodite'
import { IFRAME_CLASSNAMES } from '../../../../../../common/constants/ConstantsIFrameClassNames'
import { TransactionInformation } from '../../../../../../common/types/transactionInformationTypes/TransactionInformation'
import {
  getVoucherCode,
  isVoucherExpired
} from '../../../services/voucherService'
import { PartnerSuppliedInfo } from '../../../../../../common/types/partnerSuppliedInfoTypes/PartnerSuppliedInfo'
import { D } from '../../../../../../common/constants/ConstantsLogs'
import { _devLog } from '../../../../../../common/utilities/DevLoggingUtility'

/**
 * Props Interface for OfferTermsLink
 */
interface OfferTermsLinkProps {
  styles: { [key: string]: any }
  linkText: string
  areTermsVisible: boolean
  setTermsVisible: (areTermsVisible: boolean) => void
  partnerSuppliedInfo: PartnerSuppliedInfo
  transactionInformation: TransactionInformation
  setTransactionInformation: (
    transactionInformation: TransactionInformation
  ) => void
  setDoesPartnerNeedUpdateForTransactionInformation: (b: boolean) => void
}

/**
 * The link to the offer's terms and conditions.
 *
 * @param OfferTermsLinkProps
 * @returns
 */
const OfferTermsLink: FunctionalComponent<OfferTermsLinkProps> = ({
  styles,
  linkText,
  areTermsVisible,
  setTermsVisible,
  partnerSuppliedInfo,
  transactionInformation,
  setTransactionInformation,
  setDoesPartnerNeedUpdateForTransactionInformation
}) => {
  // return component
  return (
    <a
      class={`${IFRAME_CLASSNAMES.BPERX_SELECTED_OFFER_TERMS_LINK}${' '}${
        styles[IFRAME_CLASSNAMES.BPERX_SELECTED_OFFER_TERMS_LINK] === undefined
          ? ''
          : css(styles[IFRAME_CLASSNAMES.BPERX_SELECTED_OFFER_TERMS_LINK])
      }`}
      onClick={(e: Event) => {
        setTermsVisible(!areTermsVisible)

        // get a new voucher if the voucher is missing or expired
        if (
          transactionInformation.voucherInformation.voucherCode === undefined ||
          transactionInformation.voucherInformation.voucherCode === null ||
          (transactionInformation.voucherInformation.voucherCode !==
            undefined &&
            isVoucherExpired(
              transactionInformation.voucherInformation.voucherExpirationDateUTC
            ))
        ) {
          getVoucherCode(
            partnerSuppliedInfo,
            transactionInformation,
            setTransactionInformation,
            setDoesPartnerNeedUpdateForTransactionInformation
          )
          _devLog(
            D.VOUCHER_GENERATED_TERMS,
            partnerSuppliedInfo.frameInformation.partnerId,
            undefined
          )
        }
      }}
    >
      <span
        class={`${IFRAME_CLASSNAMES.BPERX_SELECTED_OFFER_TERMS_CHEVRON}${' '}${
          styles[IFRAME_CLASSNAMES.BPERX_SELECTED_OFFER_TERMS_CHEVRON] ===
          undefined
            ? ''
            : css(styles[IFRAME_CLASSNAMES.BPERX_SELECTED_OFFER_TERMS_CHEVRON])
        }`}
      >
        &gt;
      </span>
      {linkText}
    </a>
  )
}

export default OfferTermsLink
